import { LogInit } from "@/libs/logger";
import { GetConfig } from "@/types/Api/Common/Home/getConfig";
import { AxiosResponse } from "axios";
import CryptoJS from "crypto-js";
import AES from "crypto-js/aes";
import MD5 from "crypto-js/md5";
import { pureAxios } from "../utils/request";
import { dateFormat } from "../utils/tool";
import * as Sentry from '@sentry/react';


function deCryptogram(str: string, MD5_KEY: string) {
  // AES-256-ECB
  const bytes = AES.decrypt(str, CryptoJS.enc.Utf8.parse(MD5_KEY as string), {
    mode: CryptoJS.mode.ECB,
  });
  const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
  return decryptedData as any;
}

function any<D>(promises: Promise<D>[]) {
  // return a Promise, which resolves as soon as one promise resolves
  return new Promise<D>((resolve, reject) => {
    let isFulfilled = false;
    const errors: Error[] = [];
    let errorCount = 0;
    promises.forEach((promise, index) =>
      promise.then(
        (data) => {
          if (!isFulfilled) {
            resolve(data);
            isFulfilled = true;
          }
        },
        (error: Error) => {
          errors[index] = error;
          errorCount += 1;

          if (errorCount === promises.length) {
            reject(error);
          }
        }
      )
    );
  });
}

type FetchListFn = (url: string) => Promise<any>;
/* `const fetchList`
是一个接受字符串数组和函数作为参数的函数。它返回一个承诺，当它成功地从数组中的一个字符串中获取数据时，该承诺将通过提供的函数的响应进行解析。如果该函数无法从数组中的所有字符串中获取数据，则该承诺会因错误而被拒绝。此函数的目的是尝试从多个来源获取数据，直到成功，或者直到所有来源都已用尽。 */
const fetchList = <T, D = any>(
  list: string[],
  fn: FetchListFn
): Promise<[D | AxiosResponse<T>, string, number]> => {
  const promiseList: Promise<[D | AxiosResponse<T>, string, number]>[] = [];
  return new Promise((resolve, reject) => {
    list.forEach((url: string, index: number) => {
      const doFetch = () => {
        return new Promise<[D | AxiosResponse<T>, string, number]>(
          (resolve, reject) => {
            fn(url)
              .then((response: D | AxiosResponse<T>) => {
                resolve([response, url, index]);
              })
              .catch((err) => {
                // TODO: 需要更具体的报错信息？
                console.error(...LogInit.error(err));
              })
              .then(() => {
                reject(
                  new Error("index out of list range at function doFetch")
                );
              });
          }
        );
      };
      promiseList.push(doFetch());
    });
    any<[D | AxiosResponse<T>, string, number]>(promiseList)
      .then((res) => resolve(res))
      .catch((e) => reject(e));
  });
};

/**
 * 请求桶数据
 * @returns Promise<url:stirng, void>
 */
/* `const fetchBucket` 是一个函数，它根据当前时间和一个密钥生成一个唯一的桶密钥，然后使用这个密钥从三个不同的云存储服务中获取数据。它使用 fetchList
函数依次尝试从每个服务中获取数据，直到成功或所有服务都已用完。如果无法从任何服务获取数据，它会记录错误并可能将其报告给 Sentry 等服务。 */
const fetchBucket = (): Promise<{ api_url: string[]; image_cdn: string[] }> => {
  const time = dateFormat(new Date(), "YMDh");
  const titles = window.document.getElementsByTagName("title");
  const title = titles[0] as any;
  const keys = title.config();
  const bktUrls = title.buckets(
    keys.map(({ dk, dp }: { dk: string, dp: string }) => ({
      key: MD5(time + dk)
        .toString()
        .substring(8, 26),
      png: dp,
    }))
  );
  let isFinished = false;
  let echoCounter = 0;
  return new Promise<{ api_url: string[]; image_cdn: string[] }>((resolve) => {
    const checkEcho = () => {
      if (isFinished) return;
      if (echoCounter >= bktUrls.length) {
        // 检查网络权限
        const isH5Plus = !!window.plus;
        if (isH5Plus) {
          const plus = window.plus as any;
          const networkStatus = plus.networkinfo.getCurrentType();
          const isConnected = (() => {
            switch (networkStatus) {
              case plus.networkinfo.CONNECTION_UNKNOW:
              case plus.networkinfo.CONNECTION_NONE:
                return false;
              case plus.networkinfo.CONNECTION_ETHERNET:
              case plus.networkinfo.CONNECTION_WIFI:
              case plus.networkinfo.CONNECTION_CELL2G:
              case plus.networkinfo.CONNECTION_CELL3G:
              case plus.networkinfo.CONNECTION_CELL4G:
              case plus.networkinfo.CONNECTION_CELL5G:
                return true;
              default:
                return false;
            }
          })();
          const noticeStr = isConnected
            ? `网络异常，请检查网络链接或网络权限是否开启！[${networkStatus}]`
            : `当前无网络![${networkStatus}]`;

          plus.nativeUI.confirm(
            noticeStr,
            (e: any) => {
              if (e.index === 0) plus.runtime.restart();
              if (e.index === 1) plus.runtime.quit();
            },
            { buttons: ["重启", "退出"] }
          );
        } else {
          window.alert(`bkt资源加载失败，请切换网络重新尝试`);
          window.location.reload();
        }
      }
    };

    const fetchBucket = (url: string, dk: string) => {
      pureAxios
        .get(url)
        .then((response) => {
          return response.data;
        })
        .then((data) => {
          if (!isFinished) {
            let decrypted;
            let isError = false;
            try {
              decrypted = deCryptogram(data, dk);
            } catch (error) {
              isError = true;
            }
            if (!isError && !!decrypted) {
              resolve(decrypted);
              isFinished = true;
              return decrypted;
            }
          }
        })
        .catch((err) => console.log("fetch bucket failed", err))
        .then(() => {
          console.warn("increase echo counter");
          echoCounter++;
          checkEcho();
        });
    };

    const run = () => {
      for (let index = 0; index < bktUrls.length; index++) {
        const element = bktUrls[index];
        fetchBucket(element, keys[index % keys.length].dk);
      }
    };

    run();
  });
};

/* `const fetchApi` 是一个将 URL 数组作为输入并返回承诺的函数。它使用 `fetchList` 函数尝试从数组中的每个 URL 获取数据，如果成功，它会向 API 端点
`/Api/Common/Home/getConfig` 发送 GET 请求。如果它无法从所有 URL 获取数据，它就会拒绝承诺并报错。如果它成功地从任何 URL 获取数据但未能从 API
端点获得响应，它也会以错误拒绝承诺。 */
const fetchApi = (urls: string[]): Promise<[GetConfig["response"], string]> => {
  return new Promise((resolve, reject) => {
    fetchList<GetConfig["response"]>(urls, (url) =>
      pureAxios.get(url.replace(/\/$/gim, "") + "/Api/Common/Home/getConfig")
    )
      .then(([response, url]) => {
        if (response.data.code === 200) {
          resolve([response.data.result, url]);
        } else {
          reject(
            new Error(
              `can not fetch init api from /Api/Common/Home/getConfig; msg:${response.data.msg}`
            )
          );
        }
      })
      .catch((err) => {
        // TODO API 初始化失败；需要上报 sentry
        // TODO 给予提示 API 初始化失败
        Sentry.captureException(err, {
          tags: {
            capture_fn: "API 初始化失败",
          },
        });
        reject(err);
      });
  });
};

/* `const fetchCDN` 是一个函数，它将一组 CDN URL 和一个可选的图像路径作为输入并返回一个承诺。它使用 `fetchList` 函数尝试从数组中的每个 CDN URL
中获取图像，直到它成功或所有 URL 都已用完。如果它未能从任何 URL 获取图像，它就会拒绝承诺并报错。 */
export const fetchCDN = (CDNs: string[], img = ""): Promise<string> => {
  return new Promise((resolve, reject) => {
    fetchList<undefined, string>(CDNs, (url) => {
      return pureAxios.get(
        url.replace(/\/$/gim, "") + "/" + img.replace(/^\//gim, "")
      );
    })
      .then(([_, url]) => resolve(url as string))
      .catch((err) => {
        // TODO CDN 初始化失败；需要上报 sentry
        Sentry.captureException(err, {
          tags: {
            capture_fn: "CDN 初始化失败",
          },
        });
        reject(err);
      });
  });
};

export const init = (): Promise<{
  data: GetConfig["response"];
  cdns: string[];
  api: string;
}> => {
  return new Promise((resolve, reject) => {
    fetchBucket()
      .then((data) => {
        let urls =   { api_url :[], image_cdn :[] };
        try{
          urls = JSON.parse(data as any);
        }catch(e){}
        const { api_url = [], image_cdn = [] } = urls;
        fetchApi(api_url)
          .then(([data, url]) => resolve({ data, cdns: image_cdn, api: url }))
          .catch((err) => {
            console.error(...LogInit.error(err));
            reject(err);
          });
      })
      .catch((err) => {
        console.error(...LogInit.error(err));
        reject(err);
      });
  });
};
